import React, { useState } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import ChoshiiLogo from '../../ChoshiiLogo';
import { useTranslation } from 'react-i18next';
type AdvertiserStatus = {
  completed: boolean;
  approved: boolean;
  suspended: boolean;
};
const GOLD = '#D4AF37';
const TEXT_DEFAULT = '#ccc';
const BG = '#111';
const BORDER_DEFAULT = '#222';

const SidebarAds: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
const { logout, user } = useAuth();
  const { t } = useTranslation();
const [showInactiveModal, setShowInactiveModal] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/ads-home');
  };


  const advertiserInactive =
  !user?.advertiserProfile?.completed ||
  !user?.advertiserProfile?.approved ||
  !!user?.advertiserProfile?.suspended;

const handleCreateCampaign = () => {
  if (advertiserInactive) {
    setShowInactiveModal(true);
    return;
  }

  navigate("/adupload", { state: { reset: true } });
};

  // ---- active-route helper
  const isActive = (path: string | string[]) => {
    const paths = Array.isArray(path) ? path : [path];
    return paths.some((p) =>
      p === '/' ? routerLocation.pathname === '/' : routerLocation.pathname.startsWith(p)
    );
  };

  // ---- full-width sidebar button
  const Button: React.FC<{
    to?: string;
    onClick?: () => void;
    children: React.ReactNode;
    activeMatch?: string | string[];
  }> = ({ to, onClick, children, activeMatch }) => {
    const [hovered, setHovered] = useState(false);
    const active = activeMatch ? isActive(activeMatch) : !!(to && isActive(to));

    const style: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',       // ✅ centers icon vs multi-line text block
      gap: 8,
      width: '100%',
      padding: '8px 10px',
      marginBottom: '8px',
    
      whiteSpace: 'normal',       // ✅ allow wrap
      overflow: 'visible',        // ✅ no clipping
      textOverflow: 'clip',       // ✅ no "..."
    
      backgroundColor: BG, // ✅ always dark
      color: TEXT_DEFAULT, // ✅ always light
      border: `1px solid ${active ? GOLD : hovered ? GOLD : BORDER_DEFAULT}`, // ✅ active = permanent gold border
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      textAlign: 'left',
      fontSize: 14,
      transition: 'border-color 120ms ease',
    };
    
    return (
      <button
        type="button"
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => (to ? navigate(to) : onClick?.())}
      >
        {children}
      </button>
    );
  };

  // ---- gold CTA button (Create new campaign)
  const GoldButton: React.FC<{
    to?: string;
    onClick?: () => void;
    children: React.ReactNode;
  }> = ({ to, onClick, children }) => {
    const [hovered, setHovered] = useState(false);

    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '8px 10px',
      marginBottom: '8px',
      backgroundColor: GOLD,
      color: '#000',
      border: `1px solid ${GOLD}`,
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 600,
      textAlign: 'left',
      fontSize: 14,
      boxShadow: hovered ? '0 0 18px rgba(212, 175, 55, 0.25)' : 'none',
      transition: 'box-shadow 120ms ease, transform 120ms ease',
      transform: hovered ? 'translateY(-1px)' : 'translateY(0px)',
    };

    return (
      <button
        type="button"
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => (to ? navigate(to) : onClick?.())}
      >
        {children}
      </button>
    );
  };

  const RED = '#D24444';

  const DangerButton: React.FC<{
    onClick?: () => void;
    children: React.ReactNode;
  }> = ({ onClick, children }) => {
    const [hovered, setHovered] = useState(false);

    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '8px 10px',
      marginBottom: '8px',
      backgroundColor: BG,
      color: TEXT_DEFAULT,
      border: `1px solid ${hovered ? RED : BORDER_DEFAULT}`,
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      textAlign: 'left',
      fontSize: 14,
      transition: 'border-color 120ms ease',
    };

    return (
      <button
        type="button"
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={onClick}
      >
        {children}
      </button>
    );
  };

  return (
    <>
      {/* Sidebar */}
      <aside style={sidebarStyle}>
        {/* Top bar inside Sidebar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            marginBottom: '-20px',
            marginTop: '-2.7%',
            color: '#fff',
          }}
        >
          {/* Logo */}
          <div style={{ marginLeft: '-39.5%', marginTop: '12.5%', marginBottom: '13%' }}>
            <ChoshiiLogo
              animateSheen
              fontFamily="'Playfair Display', serif"
              style={{ width: '99%' }}
              padding={0}
              onClick={() => navigate('/advertisementstart')}
              clickTrimRatio={0.12}
            />
          </div>
        </div>

        <hr
          style={{
            margin: '7px 0',
            border: '0',
            borderTop: '1px solid #000',
          }}
        />

        <nav>
          {/* NEW: Ads quick actions */}

<Button to="/adregistration" activeMatch="/adregistration">
                    📒 "Advertise Info"
          </Button>

<Button to="/managebillings" activeMatch="/managebillings">
            💵 {t('ad.start.billing.manageBillings', 'Manage Billings')}
          </Button>


          <Button to="/advertisementstart" activeMatch="/advertisementstart">
            🛍️ {t('mainSidebar.buttons.ads')}
          </Button>     

          



          <Button to="/changepayment" activeMatch="/changepayment">
            <span style={{ display: 'inline-flex', width: 20, justifyContent: 'center', marginLeft: -4, }}>💳</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: -4, }}>
              {t('ad.start.billing.changePayment', 'Change Payment Method')}
            </span>
          </Button>
          <hr style={{ margin: '16px 0', borderColor: '#333' }} />

          <Button onClick={handleCreateCampaign}>
            <span style={{ display: 'inline-flex', width: 20, justifyContent: 'center', marginLeft: -5, }}>➕</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: -5, }}>
              {t('ad.start.buttons.newCampaign', 'Create new campaign')}
            </span>
          </Button>
          
          <hr style={{ margin: '16px 0', borderColor: '#333' }} />

          <Button to="/ad/settings/overview" activeMatch="/ad/settings/overview">
            ⚙️ {t('mainSidebar.buttons.settings')}
          </Button>
        </nav>

        <hr style={{ margin: '16px 0', borderColor: '#333' }} />

        <DangerButton onClick={handleLogout}>
          📕 {t('mainSidebar.buttons.logout')}
        </DangerButton>

        {/* Ads button removed for Ads sidebar (we're already in ads area) */}
      </aside>

        {showInactiveModal && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    }}
  >
    <div
      style={{
        background: "#0D101B",
        border: "1px solid #22283a",
        borderRadius: 16,
        padding: 24,
        width: "100%",
        maxWidth: 420,
        boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>
        Advertiser account not active
      </h3>

      <p style={{ fontSize: 14, color: "#9ca3af", lineHeight: 1.5 }}>
        Your advertiser account is not active yet.  
        You must complete advertiser onboarding and billing before you can
        publish ads.
      </p>

      <div
        style={{
          marginTop: 20,
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <button
          onClick={() => setShowInactiveModal(false)}
          style={{
            padding: "10px 16px",
            borderRadius: 999,
            border: "1px solid #4b5563",
            background: "transparent",
            color: "#e5e7eb",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        <button
          onClick={() => {
            setShowInactiveModal(false);
            navigate("/adupload");
          }}
          style={{
            padding: "10px 18px",
            borderRadius: 999,
            background: "#22c55e",
            color: "#020617",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
          }}
        >
          Continue
        </button>
      </div>
    </div>
  </div>
)}

    </>
  );
};

const sidebarStyle: React.CSSProperties = {
  width: '160px',
  background: 'linear-gradient(145deg, rgba(5, 9, 20, 0.95), rgba(4, 7, 15, 0.95))',
  color: '#bbb',
  padding: '20px',
  height: 'calc(100vh - 50px)',
  boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  position: 'fixed',
  marginTop: '-2%',
  overflow: 'hidden',
};

export default SidebarAds;
